/**
 * Legal document loader and frontmatter parser.
 *
 * Documents are inlined as strings at build time (no runtime fetch).
 * This avoids issues with hosting rewrite rules intercepting .md URLs.
 *
 * To update a document: edit the .ts file in src/documents/. Update the
 * issuedDate in the frontmatter to trigger re-acceptance for all users.
 */

import { TERMS_OF_SERVICE_MD } from "../documents/terms-of-service";
import { PRIVACY_POLICY_MD } from "../documents/privacy-policy";

export interface LegalDocument {
  title: string;
  issuedDate: string; // e.g. "2026-05-01"
  content: string; // markdown body without frontmatter
}

// ---------------------------------------------------------------------------
// Frontmatter parser
// ---------------------------------------------------------------------------

export function parseFrontmatter(raw: string): LegalDocument {
  const fmRegex = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/;
  const match = raw.trim().match(fmRegex);

  if (!match) {
    return { title: "", issuedDate: "", content: raw.trim() };
  }

  const frontmatter = match[1];
  const content = match[2].trim();

  const titleMatch = frontmatter.match(/^title:\s*(.+)$/m);
  const dateMatch = frontmatter.match(/^issuedDate:\s*"?([^"\n]+)"?$/m);

  return {
    title: titleMatch ? titleMatch[1].trim() : "",
    issuedDate: dateMatch ? dateMatch[1].trim() : "",
    content,
  };
}

// ---------------------------------------------------------------------------
// Document accessors (parsed once, cached)
// ---------------------------------------------------------------------------

let _termsOfService: LegalDocument | null = null;
let _privacyPolicy: LegalDocument | null = null;

/**
 * Parse and cache both legal documents. Kept async for API compatibility
 * with existing callers, but resolves immediately (no network requests).
 */
export async function loadDocuments(): Promise<void> {
  if (_termsOfService && _privacyPolicy) return;

  _termsOfService = parseFrontmatter(TERMS_OF_SERVICE_MD);
  _privacyPolicy = parseFrontmatter(PRIVACY_POLICY_MD);
}

export function getTermsOfService(): LegalDocument {
  if (!_termsOfService) {
    throw new Error("Documents not loaded. Call loadDocuments() first.");
  }
  return _termsOfService;
}

export function getPrivacyPolicy(): LegalDocument {
  if (!_privacyPolicy) {
    throw new Error("Documents not loaded. Call loadDocuments() first.");
  }
  return _privacyPolicy;
}
